import { parseQueryParamToNumbers } from 'utils/url';
import { profileGroupEligibleForAnalyticsPicker } from 'app/modules/analytics/utils/analyticsNonGlobalProfileGroups';
import type { PageData, ProfileGroup } from 'types/Page';

export const ANALYTICS_SELECTION_QUERY_KEYS = {
  profiles: 'profiles',
  groups: 'groups',
} as const;

export type AnalyticsSelection =
  | { pageId: number; groupId: null }
  | { pageId: null; groupId: number }
  | { pageId: null; groupId: null };

export interface AnalyticsPersistedInput {
  pageId: number | null;
  groupId: number | null;
}

export function resolveAnalyticsSelection(
  search: string,
  pages: PageData[],
  profileGroups: ProfileGroup[],
  persisted: AnalyticsPersistedInput,
): AnalyticsSelection {
  const params = new URLSearchParams(search);
  const hasProfileParam = params.has(ANALYTICS_SELECTION_QUERY_KEYS.profiles);
  const hasGroupParam = params.has(ANALYTICS_SELECTION_QUERY_KEYS.groups);
  const hasAnyUrlSelectionKey = hasProfileParam || hasGroupParam;

  const groupIds = parseQueryParamToNumbers(
    params.get(ANALYTICS_SELECTION_QUERY_KEYS.groups),
  );
  const profileIds = parseQueryParamToNumbers(
    params.get(ANALYTICS_SELECTION_QUERY_KEYS.profiles),
  );
  const firstGroupId = groupIds[0];
  const firstProfileId = profileIds[0];

  if (hasAnyUrlSelectionKey) {
    if (
      Number.isFinite(firstGroupId) &&
      profileGroups.some(
        (g) =>
          g.id === firstGroupId && profileGroupEligibleForAnalyticsPicker(g),
      )
    ) {
      return { pageId: null, groupId: firstGroupId };
    }
    if (
      Number.isFinite(firstProfileId) &&
      pages.some((p) => p.id === firstProfileId)
    ) {
      return { pageId: firstProfileId, groupId: null };
    }
  }

  if (
    persisted.groupId !== null &&
    profileGroups.some(
      (g) =>
        g.id === persisted.groupId && profileGroupEligibleForAnalyticsPicker(g),
    )
  ) {
    return { pageId: null, groupId: persisted.groupId };
  }

  if (
    persisted.pageId !== null &&
    pages.some((p) => p.id === persisted.pageId)
  ) {
    return { pageId: persisted.pageId, groupId: null };
  }

  if (pages.length > 0) {
    return { pageId: pages[0].id, groupId: null };
  }

  return { pageId: null, groupId: null };
}

/** Writes both selection keys from state, so repeating the call is a no-op. */
export function applyAnalyticsSelectionToQuery(
  params: URLSearchParams,
  pageId: number | null,
  groupId: number | null,
): void {
  if (groupId !== null) {
    params.delete(ANALYTICS_SELECTION_QUERY_KEYS.profiles);
    params.set(ANALYTICS_SELECTION_QUERY_KEYS.groups, String(groupId));
    return;
  }

  params.delete(ANALYTICS_SELECTION_QUERY_KEYS.groups);

  if (pageId !== null) {
    params.set(ANALYTICS_SELECTION_QUERY_KEYS.profiles, String(pageId));
    return;
  }

  params.delete(ANALYTICS_SELECTION_QUERY_KEYS.profiles);
}
